import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { Student } from '../../shared-components/student/student.model';
import { StudentService } from '../../shared-components/student/student.service';
import { User } from '../../shared-components/user/user.model';
import { YearService } from '../../shared-components/year/year.service';
import { LoginService } from '../../shared-modules/login/login.service';
import { DentalComponent } from './dental/dental.component';
import { DoctorsComponent } from './doctors/doctors.component';
import { HealthVisitLogComponent } from './health-visit-log/health-visit-log.component';
import { CodeLegend } from './health.model';
import { HealthService } from './health.service';
import { HearingComponent } from './hearing/hearing.component';
import { ImmunizationsComponent } from './immunizations/immunizations.component';
import { InsuranceComponent } from './insurance/insurance.component';
import { MedicationsComponent } from './medications/medications.component';
import { PhysicalExamComponent } from './physical-exam/physical-exam.component';
import { CodeLegendComponent } from './screening/code-legend/code-legend.component';
import { SpinalComponent } from './spinal/spinal.component';
import { VisionComponent } from './vision/vision.component';

@AutoUnsubscribe()
@Component({
    selector: 'app-health',
    templateUrl: './health.component.html',
    styleUrls: []
})
export class HealthComponent implements OnInit, OnDestroy {

    // ------------Element references--------------------------------
    @ViewChild('tabContent', { read: ElementRef }) tabContentRef: ElementRef;
    @ViewChild(DoctorsComponent) doctorsComponent: DoctorsComponent;
    @ViewChild(HealthVisitLogComponent) healthVisitLogComponent: HealthVisitLogComponent;
    @ViewChild(ImmunizationsComponent) immunizationsComponent: ImmunizationsComponent;
    @ViewChild(InsuranceComponent) insuranceComponent: InsuranceComponent;
    @ViewChild(MedicationsComponent) medicationsComponent: MedicationsComponent;
    @ViewChild(HearingComponent) hearingComponent: HearingComponent;
    @ViewChild(VisionComponent) visionComponent: VisionComponent;
    @ViewChild(DentalComponent) dentalComponent: DentalComponent;
    @ViewChild(SpinalComponent) spinalComponent: SpinalComponent;
    @ViewChild(PhysicalExamComponent) physicalExamComponent: PhysicalExamComponent;

    // ------------Selection/Flags-----------------------------------
    expandEmitter: boolean;
    allowMultiSelect = true;
    setIndex = new EventEmitter<number>();
    selectedTabName: string;

    // ------------------------------------------------------------
    currentUser: User;
    selectedStudent: Student;
    routeSelectedTabIndex: number;
    selectedStudentSubscription: Subscription;
    student360YearSubscription: Subscription;
    studentEnrollment = this.studentService.studentEnrollment;
    legendCodes: any = [];
    initialSelection = [];
    legendDialogRef: MatDialogRef<CodeLegendComponent>;
    toggleAllRows: EventEmitter<boolean> = new EventEmitter<boolean>();
    selection = new SelectionModel<any>(this.allowMultiSelect, this.initialSelection);
    tabNames: string[] = [];

    constructor(
        private studentService: StudentService,
        private yearService: YearService,
        private healthService: HealthService,
        private dialog: MatDialog,
        private loginService: LoginService,
        private activatedRoute: ActivatedRoute
    ) {
        this.selectedStudentSubscription = studentService.selectedStudentStream$.subscribe(
            studentId => {
                this.loadStudentInfo(studentId);
            });

        this.student360YearSubscription = this.yearService.observableStudent360YearStream$.subscribe(() => {
            this.getStudentHealthPortals();
        });
    }

    ngOnInit() {
        if (this.loginService.LoginSettings.S360DisplayImmunizations) {
            this.tabNames.push('Immunizations');
        }
        if (this.loginService.LoginSettings.S360DisplayMedications) {
            this.tabNames.push('Medications');
        }
        if (this.loginService.LoginSettings.S360DisplayHealthVisitLog) {
            this.tabNames.push('Health Visit Log');
        }
        if (this.loginService.LoginSettings.S360DisplayDoctors) {
            this.tabNames.push('Practitioners');
        }
        if (this.loginService.LoginSettings.S360DisplayInsurane) {
            this.tabNames.push('Insurance');
        }
        if (this.loginService.LoginSettings.S360DisplayScreenings) {
            this.tabNames.push('Screenings');
        }
        if (this.tabNames.length > 0) {
            this.selectedTabName = this.tabNames[0];
        }
        this.activatedRoute.queryParams.subscribe(params => {
            const selectedTab = params['selectedTab'];
            if (selectedTab) {
                for (let i = 0; i < this.tabNames.length; i++) {
                    if (this.tabNames[i] == selectedTab) {
                        this.routeSelectedTabIndex = i;
                        setTimeout(() => {
                            this.setIndex.emit(this.routeSelectedTabIndex);
                        }, 500);
                    }
                }

                if (!this.loginService.LoginSettings.V10) {
                    this.selectedTabName = selectedTab;
                }
            }
        });
        this.studentEnrollment = this.studentService.studentEnrollment;
        this.loadStudentInfo(this.studentService.selectedStudent.Id);
    }

    ngOnDestroy() {
        this.selectedStudentSubscription.unsubscribe();
    }

    loadStudentInfo(studentId: number) {
        this.selectedStudent = this.studentService.students.filter(function (student) {
            return student.Id == studentId;
        })[0];
        this.getStudentHealthPortals();
    }

    getStudentHealthPortals() {
        if (!this.loginService.LoginSettings.V10 && this.selectedStudent) {
            this.healthService.getStudentHealthPortals(this.selectedStudent.Id, this.yearService.defaultYear.Id).subscribe(result => {
                this.tabNames = result;

                if (this.tabNames.length > 0) {
                    if (this.selectedTabName && this.selectedTabName != '') {
                        let tabIndex = this.tabNames.indexOf(this.selectedTabName);
                        if (tabIndex < 0) {
                            tabIndex = 0;
                        }
                        this.setIndex.emit(tabIndex);
                    }
                    else {
                        this.selectedTabName = this.tabNames[0];
                    }
                }
            });
        }
    }

    selectionChange(selectionIndex: number) {
        this.selectedTabName = this.tabNames[selectionIndex];
    }

    expandAll(expand: boolean) {
        if (this.selectedTabName == 'Medications') {
            this.medicationsComponent.expandAll(expand);
        }
    }

    isCurrentTabFiltered(): boolean {
        switch (this.selectedTabName) {
            case 'Immunizations':
                if (this.immunizationsComponent) {
                    return this.immunizationsComponent.angularMatData.isFiltered();
                }
                break;
            case 'Medications':
                if (this.medicationsComponent) {
                    return this.medicationsComponent.angularMatData.isFiltered();
                }
                break;
            case 'Health Visit Log':
                if (this.healthVisitLogComponent) {
                    return this.healthVisitLogComponent.angularMatData.isFiltered();
                }
                break;
            case 'Practitioners':
                if (this.doctorsComponent) {
                    return this.doctorsComponent.angularMatData.isFiltered();
                }
                break;
            case 'Insurance':
                if (this.insuranceComponent) {
                    return this.insuranceComponent.angularMatData.isFiltered();
                }
                break;
            case 'Hearing':
                if (this.hearingComponent) {
                    return this.hearingComponent.angularMatData.isFiltered();
                }
                break;
            case 'Vision':
                if (this.visionComponent) {
                    return this.visionComponent.angularMatData.isFiltered();
                }
                break;
            case 'Dental':
                if (this.dentalComponent) {
                    return this.dentalComponent.angularMatData.isFiltered();
                }
                break;
            case 'Spinal':
                if (this.spinalComponent) {
                    return this.spinalComponent.angularMatData.isFiltered();
                }
                break;
            case 'Physical Exam':
                if (this.physicalExamComponent) {
                    return this.physicalExamComponent.angularMatData.isFiltered();
                }
                break;
            default:
                break;
        }
        return false;
    }

    clearCurrentTabFilters() {
        switch (this.selectedTabName) {
            case 'Immunizations':
                if (this.immunizationsComponent) {
                    this.immunizationsComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Medications':
                if (this.medicationsComponent) {
                    this.medicationsComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Health Visit Log':
                if (this.healthVisitLogComponent) {
                    this.healthVisitLogComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Practitioners':
                if (this.doctorsComponent) {
                    this.doctorsComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Insurance':
                if (this.insuranceComponent) {
                    this.insuranceComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Hearing':
                if (this.hearingComponent) {
                    return this.hearingComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Vision':
                if (this.visionComponent) {
                    return this.visionComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Dental':
                if (this.dentalComponent) {
                    return this.dentalComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Spinal':
                if (this.spinalComponent) {
                    return this.spinalComponent.angularMatData.removeAllFilters();
                }
                break;
            case 'Physical Exam':
                if (this.physicalExamComponent) {
                    return this.physicalExamComponent.angularMatData.removeAllFilters();
                }
                break;
            default:
                break;
        }
    }

    // ------------Modal window-----------------------
    codeLegendClick() {
        let legendCodes: CodeLegend[] = [];
        this.healthService.getScreeningCodeLegend(this.selectedStudent.Id).subscribe(data => {
            legendCodes = data;

            this.legendDialogRef = this.dialog.open(CodeLegendComponent, {
                panelClass: 'tyl-mat-dialog',
                minWidth: '25%',
                maxWidth: '25%',
                minHeight: '25%',
                maxHeight: '50%',
                data: {
                    legendCodes: legendCodes
                }
            });

            this.legendDialogRef.componentInstance.legendCodes = legendCodes;
            this.legendDialogRef.afterClosed().subscribe(result => {
            });
        });
    }
}
